// ============================================================================
// MachineActionCard — a step on one floor machine from Ask AI (item #944, the
// fleet part), built like the other part cards to Ben's approved card demo
// (docs/build-room/demos/item-944.html). What it shows is machineCard() in
// ../fleet-actions.ts; the save is commitMachineAction().
// ============================================================================

import { useMemo, useState } from 'react';
import { Wrench } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { CopilotProposalBase } from '../../../shared/types';
import { machineCard, machineOf, type MachineDraft } from '../fleet-actions';
import { CardShell } from './card-shell';
import { FieldControl } from './building-action-card';

interface Props {
  proposal: CopilotProposalBase;
  isCommitting: boolean;
  onDoIt: (edited_input: Record<string, unknown>, was_edited: boolean) => void;
  onDiscard: () => void;
}

export function MachineActionCard({ proposal, isCommitting, onDoIt, onDiscard }: Props) {
  const draft = proposal.input as unknown as MachineDraft;
  const start = useMemo(() => ({ ...(draft.fields ?? {}) }), [draft]);
  const [fields, setFields] = useState<Record<string, unknown>>(start);
  const card = machineCard(draft, fields);
  const wasEdited = JSON.stringify(fields) !== JSON.stringify(start);

  return (
    <CardShell
      title={`${card.title} · ${machineOf(draft)}`}
      icon={<Wrench className="h-4 w-4" />}
      accentClass={card.readBack ? 'border-st-warn-border-strong' : 'border-tcs-border'}
      status={proposal.status}
      committedLabel="Saved"
      isCommitting={isCommitting}
      doItDisabled={!!card.missing}
      doItDisabledReason={card.missing ?? undefined}
      readBack={card.readBack ?? undefined}
      doItLabel={card.doItLabel}
      onDoIt={() => onDoIt({ ...draft, fields }, wasEdited)}
      onDiscard={onDiscard}
      after={
        <Link to={`/fleet/equipment/${encodeURIComponent(draft.machine_id)}`} className="text-sm font-medium text-tcs-text underline">
          Open the machine
        </Link>
      }
    >
      {card.info.map((i) => (
        <div key={i.label} className="flex min-h-[44px] items-center justify-between gap-3 rounded-md border border-tcs-border px-2 py-1.5 text-sm">
          <span className="text-xs text-tcs-text-muted">{i.label}</span>
          <span className="text-right text-tcs-text">{i.value}</span>
        </div>
      ))}
      {card.fields.map((spec) => (
        <FieldControl key={spec.key} spec={spec} value={fields[spec.key]} onChange={(v) => setFields((p) => ({ ...p, [spec.key]: v }))} />
      ))}
    </CardShell>
  );
}
